import { clientWorkspaceStorage } from '../common/organizationAccessMode';
// components/WorkflowChecklist.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { WORKFLOW_CHECKLISTS } from './constants';

interface WorkflowChecklistProps {
    workflowId: string;
    workflowName: string;
}

const getInitialCheckedState = (workflowId: string): Set<string> => {
    try {
        const stored = clientWorkspaceStorage().getItem(`checklist_${workflowId}`);
        if (stored) return new Set(JSON.parse(stored));
    } catch (e) {
        console.error('Failed to load checklist state', e);
    }
    return new Set<string>();
};

export const WorkflowChecklist: React.FC<WorkflowChecklistProps> = ({ workflowId, workflowName }) => {
    const checklistData = WORKFLOW_CHECKLISTS[workflowId];
    const [isOpen, setIsOpen] = useState(false);

    const allItems = useMemo(() => {
        if (!checklistData) return [];
        return checklistData.items.flatMap(item =>
            typeof item === 'string' ? [item] : [item.title, ...(item.subItems || [])]
        );
    }, [checklistData]);

    const [checkedItems, setCheckedItems] = useState<Set<string>>(() => getInitialCheckedState(workflowId));

    useEffect(() => {
        try {
            clientWorkspaceStorage().setItem(`checklist_${workflowId}`, JSON.stringify(Array.from(checkedItems)));
        } catch (e) {
            console.error('Failed to save checklist state', e);
        }
    }, [checkedItems, workflowId]);

    const handleToggle = (item: string) => {
        const next = new Set(checkedItems);
        if (next.has(item)) next.delete(item);
        else next.add(item);
        setCheckedItems(next);
    };

    if (!checklistData) return null;

    const totalItems = allItems.length;
    const completedItems = checkedItems.size;
    const progress = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
    const isComplete = completedItems === totalItems && totalItems > 0;

    return (
        <>
            {/* Mobile backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-30 lg:hidden cursor-pointer"
                    onClick={() => setIsOpen(false)}
                />
            )}

            {/* Collapsed tab — slim right-edge trigger */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    aria-label="Open Workflow Checklist"
                    className="fixed right-0 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center gap-3 bg-indigo-700 hover:bg-indigo-600 text-white py-5 px-2.5 rounded-l-xl shadow-2xl transition-colors duration-200"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span
                        className="text-[9px] font-black uppercase tracking-[0.18em] select-none"
                        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                    >
                        Checklist
                    </span>
                    <span className={`text-[10px] font-black px-1 py-0.5 rounded ${isComplete ? 'bg-emerald-500' : 'bg-white/20'}`}>
                        {Math.round(progress)}%
                    </span>
                </button>
            )}

            {/* Sidebar panel */}
            <div
                className={`fixed right-0 top-0 bottom-0 w-72 bg-slate-900 border-l border-slate-700 shadow-2xl z-40 flex flex-col transition-transform duration-300 ease-in-out ${
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
                aria-hidden={!isOpen}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-indigo-700 border-b border-indigo-800 flex-shrink-0">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-indigo-200 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="min-w-0">
                            <p className="text-[9px] font-black text-indigo-200 uppercase tracking-widest">Workflow Checklist</p>
                            <h3 className="font-bold text-white text-xs leading-tight truncate">{workflowName}</h3>
                        </div>
                    </div>
                    <button
                        onClick={() => setIsOpen(false)}
                        aria-label="Close checklist"
                        className="p-1.5 rounded-lg text-indigo-200 hover:text-white hover:bg-indigo-600 transition-colors flex-shrink-0 ml-2"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>

                {/* Progress */}
                <div className="px-4 py-3 border-b border-slate-700 bg-slate-800/60 flex-shrink-0">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Progress</span>
                        <span className={`text-[11px] font-black ${isComplete ? 'text-emerald-400' : 'text-indigo-400'}`}>
                            {completedItems} / {totalItems}
                        </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    {isComplete && (
                        <p className="text-[10px] text-emerald-400 font-bold mt-1.5 flex items-center gap-1">
                            <span>✓</span> All steps complete
                        </p>
                    )}
                </div>

                {/* Items */}
                <div className="flex-1 overflow-y-auto py-1">
                    {checklistData.items.map((item) => {
                        if (typeof item === 'string') {
                            const checked = checkedItems.has(item);
                            return (
                                <label
                                    key={item}
                                    className={`flex items-start gap-3 px-4 py-2.5 cursor-pointer transition-colors group ${
                                        checked ? 'opacity-50' : 'hover:bg-slate-800/70'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => handleToggle(item)}
                                        className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 rounded border-slate-600 bg-slate-700 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0"
                                    />
                                    <span className={`text-xs leading-snug transition-colors ${
                                        checked ? 'text-slate-500 line-through' : 'text-slate-300 group-hover:text-white'
                                    }`}>
                                        {item}
                                    </span>
                                </label>
                            );
                        }

                        // Grouped item
                        return (
                            <div key={item.title} className="mt-1 first:mt-0">
                                <div className="px-4 py-1.5 bg-slate-800">
                                    <h4 className="text-[9px] font-black text-indigo-300 uppercase tracking-widest">{item.title}</h4>
                                </div>
                                <div className="ml-4 pl-2 border-l-2 border-indigo-800/40">
                                    {item.subItems.map((subItem: string) => {
                                        const checked = checkedItems.has(subItem);
                                        return (
                                            <label
                                                key={subItem}
                                                className={`flex items-start gap-3 px-3 py-2 cursor-pointer transition-colors group ${
                                                    checked ? 'opacity-50' : 'hover:bg-slate-800/70'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => handleToggle(subItem)}
                                                    className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 rounded border-slate-600 bg-slate-700 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0"
                                                />
                                                <span className={`text-xs leading-snug transition-colors ${
                                                    checked ? 'text-slate-500 line-through' : 'text-slate-400 group-hover:text-slate-200'
                                                }`}>
                                                    {subItem}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-slate-700 bg-slate-800/60 flex-shrink-0">
                    <button
                        onClick={() => setCheckedItems(new Set())}
                        className="w-full text-[10px] font-black text-slate-500 hover:text-slate-300 uppercase tracking-widest transition-colors py-1 rounded"
                    >
                        Reset Progress
                    </button>
                </div>
            </div>
        </>
    );
};
